import type { SurveyDocument } from '@apruv/survey-contract/types';
import type { CXRuntimeReviewRouting } from '../../services/cxRuntimeContext';

/** Synthetic display-only mappings. These are never executed or saved as company settings. */
export function previewReviewRouting(
  document: SurveyDocument,
  availability: 'sample' | 'unavailable' | 'matched' | 'matched_no_alternatives',
  draw: number = 0.5
): CXRuntimeReviewRouting {
  const empty: CXRuntimeReviewRouting = {
    enabled: false,
    destinations: [],
    fallback: 'unavailable',
  };
  const routing = document.configuration.reviewRouting;
  if (
    !routing.enabled ||
    !['sample', 'matched', 'matched_no_alternatives'].includes(availability) ||
    !Number.isFinite(draw) ||
    draw < 0 ||
    draw >= 1
  )
    return empty;
  const matched = availability === 'matched' || availability === 'matched_no_alternatives';
  const alternatives = availability === 'matched';
  const behavior = routing.matchedGoogleReviewBehavior;
  if (matched && (behavior === 'referral' || behavior === 'skip_public_review'))
    return { enabled: false, destinations: [], fallback: behavior };
  // Fixed synthetic mappings make policy choices inspectable without claiming
  // the company's integrations or review history are available.
  const alternateProvider =
    alternatives && routing.alternateProvider !== 'google' ? routing.alternateProvider : undefined;
  const alternateLocationId = routing.alternateGoogleLocation.trim();
  const alternateLocation =
    alternatives && (!alternateLocationId || alternateLocationId === 'sample-alternate');
  const rerouteProvider = matched
    ? behavior === 'alternate_provider'
      ? (alternateProvider ?? (alternatives ? 'google' : undefined))
      : behavior === 'alternate_location'
        ? alternateLocation
          ? 'google'
          : alternateProvider
        : undefined
    : undefined;
  const safeURL = (value: string) => {
    try {
      const trimmed = value.trim();
      // WHATWG URL repairs empty authorities and opaque URLs that the API rejects.
      const authority = /^https?:\/\/([^/?#]+)/i.exec(trimmed)?.[1];
      if (
        !authority ||
        /[@\\\s]/.test(authority) ||
        /[\u0000-\u0020\u007f\\]/.test(trimmed) ||
        /%(?![0-9a-f]{2})/i.test(trimmed)
      )
        return '';
      const url = new URL(trimmed);
      return ['http:', 'https:'].includes(url.protocol) &&
        !!url.hostname &&
        !url.username &&
        !url.password
        ? value.trim()
        : '';
    } catch {
      return '';
    }
  };

  // One simulated Google location, just as an invitation selects one scoped location.
  const selectedGoogleURL =
    routing.allocations
      .find(
        (item) =>
          item.provider === 'google' &&
          item.destinationSource === 'override' &&
          safeURL(item.overrideReviewUrl)
      )
      ?.overrideReviewUrl.trim() ?? 'https://reviews.example.invalid/google';
  const resolved = routing.allocations.flatMap((item) => {
    let provider = item.provider;
    let url =
      item.destinationSource === 'integration'
        ? item.provider === 'google'
          ? selectedGoogleURL
          : `https://reviews.example.invalid/${item.provider}`
        : safeURL(item.overrideReviewUrl);
    if (!url || (item.provider === 'google' && url !== selectedGoogleURL)) return [];
    let label =
      item.destinationSource === 'integration'
        ? 'Sample integration mapping'
        : item.overrideLocationLabel;
    if (matched && item.provider === 'google') {
      if (!rerouteProvider) return [];
      provider = rerouteProvider;
      url =
        provider === 'google'
          ? 'https://reviews.example.invalid/google-alternate'
          : `https://reviews.example.invalid/${provider}`;
      label = provider === 'google' ? 'Sample alternate Google location' : '';
    } else if (matched && !alternatives && item.destinationSource === 'integration') {
      return [];
    }
    return [{ id: item.id, provider, url, label, weight: item.weight }];
  });
  const candidates: typeof resolved = [];
  const seen = new Map<string, number>();
  const maxWeight = resolved.reduce(
    (max, item) => (Number.isFinite(item.weight) && item.weight > max ? item.weight : max),
    0
  );
  for (const item of resolved) {
    const weight =
      maxWeight > 0 && Number.isFinite(item.weight) && item.weight > 0
        ? item.weight / maxWeight
        : 0;
    const key = `${item.provider}\n${item.url}`;
    const index = seen.get(key);
    if (index !== undefined) {
      candidates[index].weight += weight;
      if (!candidates[index].label) candidates[index].label = item.label;
    } else {
      seen.set(key, candidates.length);
      candidates.push({ ...item, weight });
    }
  }
  let selected = candidates;
  if (routing.strategy === 'sequential') selected = candidates.slice(0, 1);
  else if (routing.strategy === 'weighted') {
    const weights = candidates.map((item) =>
      Number.isFinite(item.weight) && item.weight > 0 ? item.weight : 0
    );
    const max = Math.max(0, ...weights);
    if (!max) return empty;
    let target = draw * weights.reduce((sum, value) => sum + value / max, 0);
    let chosen = -1;
    for (let index = 0; index < candidates.length; index++) {
      const weight = weights[index] / max;
      if (weight <= 0) continue;
      chosen = index;
      if (target < weight) break;
      target -= weight;
    }
    selected = chosen < 0 ? [] : [candidates[chosen]];
  } else if (routing.strategy !== 'show_all') return empty;
  const destinations = selected.map(({ weight: _weight, ...destination }) => destination);
  return destinations.length ? { enabled: true, destinations } : empty;
}
